import type {
  SatelliteWebRenterConversationAttachmentDto,
  SatelliteWebRenterConversationMessageDto,
} from '@zg-rentals/ts-satellite-client';
import { SatelliteConversationMessageType, SatelliteUiTreatmentType } from '@zg-rentals/ts-satellite-client';
import type { ConversationMessage } from '../types/conversationMessages';

/**
 * Convert API messageType (UPPER_SNAKE e.g. LISTING_MESSAGE) to UI format (camelCase e.g. listingMessage).
 * MessageItem and satellite-components expect camelCase for switch(messageType) and system message icons.
 */
function mapApiMessageTypeToUI(apiMessageType: string): string {
  if (apiMessageType === SatelliteConversationMessageType.LISTING_MESSAGE) return 'listingMessage';
  if (apiMessageType === SatelliteConversationMessageType.PHONE_LEAD) return 'phoneLead';
  // System messages and others: TOUR_CONFIRMED -> tourConfirmed
  return apiMessageType
    .split('_')
    .map((part, i) => (i === 0 ? part.toLowerCase() : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()))
    .join('');
}

/**
 * Map API uiTreatmentType to satellite-components MESSAGE_TYPES constants.
 * API: HIGH_PRIORITY, HIGH_PRIORITY_W_LOGO, LOW_PRIORITY.
 * UI: highPriority, highPriorityWithLogo, lowPriority.
 */
function mapApiUiTreatmentTypeToUI(apiUiTreatmentType: string): string {
  if (apiUiTreatmentType === SatelliteUiTreatmentType.HIGH_PRIORITY) return 'highPriority';
  if (apiUiTreatmentType === SatelliteUiTreatmentType.HIGH_PRIORITY_W_LOGO) return 'highPriorityWithLogo';
  if (apiUiTreatmentType === SatelliteUiTreatmentType.LOW_PRIORITY) return 'lowPriority';
  return apiUiTreatmentType;
}

function mapAttachment(att: SatelliteWebRenterConversationAttachmentDto): ConversationMessage['attachments'][number] {
  return {
    ...att,
    zDocId: att.zdocId,
  };
}

/**
 * Map getMessages API message to UI message shape (messageDateMs as string, zDocId, messageType/uiTreatmentType in UI format).
 */
export function mapApiMessageToConversationMessage(msg: SatelliteWebRenterConversationMessageDto): ConversationMessage {
  return {
    ...msg,
    messageDateMs: String(msg.messageDateMs ?? ''),
    messageType: mapApiMessageTypeToUI(msg.messageType),
    uiTreatmentType: msg.uiTreatmentType ? mapApiUiTreatmentTypeToUI(msg.uiTreatmentType) : undefined,
    attachments: (msg.attachments ?? []).map(mapAttachment),
  } as ConversationMessage;
}
